/* DashClip Studio — Three.js r170 viewer with engrave preview */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';

(function () {
  const root = document.querySelector('.dashclip-studio');
  if (!root) return;
  const configEl = document.getElementById('DashclipConfig-' + root.dataset.sectionId);
  const config = configEl ? JSON.parse(configEl.textContent) : {};
  const $ = (s) => root.querySelector(s);

  const container = $('#dc-viewer');
  const canvas = $('#dc-canvas');
  const loadingEl = $('#dc-loading');
  const errorEl = $('#dc-render-error');
  if (!container || !canvas) return;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
  } catch (e) {
    loadingEl.hidden = true;
    errorEl.hidden = false;
    return;
  }
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(35, 1, 0.01, 100);
  camera.position.set(0, 0.02, 0.24);

  /* Lighting rig ported from the prototype's engine.js: hemisphere fill +
     warm key + cool rim, and PMREM softbox environments — a neutral room, plus
     a brighter studio set swapped in for solid-metal (Maverick) products. */
  const pmrem = new THREE.PMREMGenerator(renderer);
  function buildEnv(bg, panels, sigma) {
    const env = new THREE.Scene();
    env.background = new THREE.Color(bg);
    for (const [x, y, z, w, h, i] of panels) {
      const p = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(i, i, i), side: THREE.DoubleSide })
      );
      p.position.set(x, y, z);
      p.lookAt(0, 0, 0);
      env.add(p);
    }
    const tex = pmrem.fromScene(env, sigma).texture;
    env.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
    return tex;
  }
  const roomEnv = buildEnv('#737981', [
    [-70, 30, 60, 40, 120, 5], [90, 10, -40, 25, 150, 3],
    [0, 100, 0, 120, 90, 2], [0, -100, 0, 160, 120, 0.25],
  ], 0.03);
  const metalEnv = buildEnv('#414650', [
    [-95, 55, 90, 85, 150, 4.5], [65, 35, -110, 65, 170, 5.0], [90, -15, 70, 25, 135, 2.0],
    [0, 30, 150, 115, 150, 3.0], [0, 110, 0, 180, 130, 2.5], [0, -90, 0, 180, 150, 0.55],
  ], 0.04);
  scene.environment = roomEnv;

  const hemi = new THREE.HemisphereLight('#fff9f1', '#626b79', 1.7);
  scene.add(hemi);
  const key = new THREE.DirectionalLight('#fff4df', 2.4);
  key.position.set(-0.9, 1, 1.5);
  scene.add(key);
  const rim = new THREE.DirectionalLight('#dbe7ff', 2.0);
  rim.position.set(0.8, 0.4, -1);
  scene.add(rim);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 1.6;
  controls.enablePan = false;
  controls.enableZoom = false;

  const productGroup = new THREE.Group();
  scene.add(productGroup);

  /* ---------- engrave decal planes ---------- */
  const ENGRAVE_PX = 1024;
  function makeSide(side) {
    const c = document.createElement('canvas');
    c.width = c.height = ENGRAVE_PX;
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    // bump channel: mid-gray base, mask stamped darker => reads recessed under lighting
    const bc = document.createElement('canvas');
    bc.width = bc.height = ENGRAVE_PX;
    const btex = new THREE.CanvasTexture(bc);
    btex.anisotropy = 8;
    const mat = new THREE.MeshStandardMaterial({
      map: tex, transparent: true, depthWrite: false,
      bumpMap: btex, bumpScale: 0.004,
      polygonOffset: true, polygonOffsetFactor: -2,
      opacity: 0.94, // let a little product texture show through, like the reference
      roughness: 0.92, metalness: 0,
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.renderOrder = 10;
    mesh.visible = false;
    productGroup.add(mesh);
    return { canvas: c, ctx: c.getContext('2d'), tex, bctx: bc.getContext('2d'), btex, mesh, image: null };
  }
  const sides = { front: makeSide('front'), back: makeSide('back') };

  const FONT_STACKS = {
    Canela: "'CanelaArabic', 'Canela', Georgia, serif",
    Modern: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    Allura: "'Allura', cursive",
    Heritage: "'Libre Baskerville', Georgia, serif",
    Mono: "'IBM Plex Mono', monospace",
  };

  /* Artwork placement per family, ported from the prototype's engravingLayout().
     Placement (centerY/nameY) shifts per product/side so marks stay inside zones
     like the Hudson thumb opening or a wallet's restricted back face. The size
     cap defaults to the geometric limit — art may grow until it touches the
     nearest region edge — so the size slider stays responsive to 100%. An
     explicit engraveRegion.maxHeight tightens it (e.g. to re-create the
     prototype's tuned safe caps). Overrides work globally or per side
     (region.front / region.back). */
  const LAYOUT_KINDS = { dashtag: 'tag', ballmarker: 'marker', maverick: 'wallet', alexander: 'alexander', hudson: 'hudson' };

  /* ---------- procedural surface grain ---------- */
  // Same LCG noise pass as the prototype's face(): a mid-gray bump map
  // (145±8) gives metal its micro-grain under lighting, and a near-white
  // mottle map recreates the ±3 luminance variation on its aluminum faces.
  function noiseCanvas(base, swing, lo = 70, hi = 210) {
    const s = 256;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const x = c.getContext('2d');
    const img = x.createImageData(s, s);
    let seed = 729;
    for (let i = 0; i < img.data.length; i += 4) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const v = Math.max(lo, Math.min(hi, base + (seed / 4294967296 - 0.5) * swing));
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    return c;
  }
  const grainTex = new THREE.CanvasTexture(noiseCanvas(145, 48));
  grainTex.wrapS = grainTex.wrapT = THREE.RepeatWrapping;

  // per-finish color map with the grain baked into the pixels, like the
  // prototype's face() pass — guaranteed visible regardless of lighting
  const mottleCache = new Map();
  function mottledMap(colorCss) {
    let t = mottleCache.get(colorCss);
    if (t) return t;
    const s = 256;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const x = c.getContext('2d');
    x.fillStyle = colorCss;
    x.fillRect(0, 0, s, s);
    const img = x.getImageData(0, 0, s, s);
    let seed = 729;
    for (let i = 0; i < img.data.length; i += 4) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const n = (seed / 4294967296 - 0.5) * 30; // ±15 luminance grain
      img.data[i] += n;
      img.data[i + 1] += n;
      img.data[i + 2] += n;
    }
    x.putImageData(img, 0, 0);
    t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 8;
    mottleCache.set(colorCss, t);
    return t;
  }

  const ORGANIC_RE = /leather|wood|walnut|oak|maple|cherry|mahogany|bamboo|ebony|rosewood/;
  const WOOD_RE = /wood|walnut|oak|maple|cherry|mahogany|bamboo|ebony|rosewood/;
  const METAL_RE = /aluminum|metal|steel|titanium|brass|bronze|copper/;

  /* Edge grain for baked GLBs: the models only embed the photographic face
     textures, so bare lit materials (edge/trim walls) get a bump map — a
     center crop of the leather/wood swatch photo (the tag outline and hole
     sit outside the sampled region) for organic finishes, LCG grain for metal. */
  const texLoader = new THREE.TextureLoader();
  const edgeTexCache = {};
  function edgeBump(probe) {
    const kind = WOOD_RE.test(probe) ? 'wood' : ORGANIC_RE.test(probe) ? 'leather' : null;
    if (!kind) return grainTex;
    if (!(kind in edgeTexCache)) {
      const src = (config.edgeTextures || {})[kind];
      const t = src ? texLoader.load(src) : null;
      if (t) {
        t.wrapS = t.wrapT = THREE.RepeatWrapping;
        t.repeat.set(0.56, 0.35);
        t.offset.set(0.22, 0.1);
        t.anisotropy = 4;
      }
      edgeTexCache[kind] = t;
    }
    return edgeTexCache[kind] || grainTex;
  }

  function materialProbe(finish, variant, product) {
    const fmMap = product?.finishMaterials || {};
    const fmKey = Object.keys(fmMap).find((k) => k.toLowerCase() === String(finish || '').toLowerCase());
    return (
      ((fmKey && fmMap[fmKey]) || variant?.material || product?.material || '') +
      ' ' + String(finish || '')
    ).toLowerCase();
  }

  function engraveLayout(data, side, hasArt, hasText) {
    const f = String((data.product && data.product.family) || config.family || '')
      .toLowerCase().replace(/[^a-z]/g, '');
    const kind = LAYOUT_KINDS[f] || '';
    const back = side === 'back';
    const restricted = kind === 'wallet' && back;
    const l = kind === 'hudson' && !back
      ? { centerY: hasText ? 0.42 : 0.47, nameY: hasArt ? 0.66 : 0.48 }
      : {
        centerY: restricted ? (hasText ? 0.75 : 0.82) : kind === 'tag' ? (hasText ? 0.45 : 0.55) : kind === 'wallet' ? (hasText ? 0.50 : 0.56) : (hasText ? 0.43 : 0.50),
        nameY: hasArt ? (restricted ? 0.89 : kind === 'marker' ? 0.78 : 0.75) : (restricted ? 0.82 : kind === 'tag' ? 0.55 : 0.52),
      };
    const region = ((data.product && data.product.engraveRegion) || config.engraveRegion || {});
    const o = Object.assign({}, region, region[side] || {});
    if (o.centerY != null) l.centerY = o.centerY;
    if (o.nameY != null) l.nameY = o.nameY;
    // art can't be taller than twice its distance to the nearest region edge;
    // the wallet's restricted back keeps a safe cap so the size slider stays
    // responsive — overhang into the clip zone is occluded by the clip itself
    // since the decal sits at plate depth
    const edge = 2 * Math.min(l.centerY, 1 - l.centerY);
    const safe = restricted ? (hasText ? 0.16 : 0.24) : edge;
    l.maxHeight = o.maxHeight != null ? Math.min(o.maxHeight, safe) : Math.min(edge, safe);
    return l;
  }

  function drawEngrave(side, data) {
    const s = sides[side];
    const { ctx, canvas } = s;
    ctx.clearRect(0, 0, ENGRAVE_PX, ENGRAVE_PX);
    const art = data.artwork[side];
    const tCfg = data.text && data.text[side];
    const text = tCfg && tCfg.enabled ? (tCfg.text || '') : '';

    // engrave tone is material-first: organic finishes burn dark (light etch
    // only on very dark swatches), metal reads as a light anodized etch, and
    // unknown materials fall back to swatch-luminance contrast. The finish
    // name itself is probed too — finishes are often named after their
    // material (e.g. "Leather", "Walnut") when no metafields are configured
    const probe = materialProbe(data.finish, data.variant, data.product);
    // luminance must be computed on sRGB channels — THREE.Color stores
    // linear values, which would drag every mid-tone below the thresholds
    const lum = srgbLuminance(data.variant?.swatchColor);
    const engraved = ORGANIC_RE.test(probe)
      ? (lum !== null && lum < 0.28 ? '#f2ede4' : '#3a2c1c')
      : METAL_RE.test(probe)
        ? '#f2ede4'
        : lum !== null
          ? (lum > 0.45 ? '#3a2c1c' : '#f2ede4')
          : '#ffffff';
    ctx.fillStyle = engraved;

    // bump base: mid-gray; artwork + text stamp darker so light reads them recessed
    const b = s.bctx;
    b.globalAlpha = 1;
    b.fillStyle = 'rgb(145,145,145)';
    b.fillRect(0, 0, ENGRAVE_PX, ENGRAVE_PX);
    b.globalAlpha = 0.8;
    b.fillStyle = '#000';

    let hasContent = false;
    const hasArt = !!(art && art.dataUrl && s.image);
    const layout = engraveLayout(data, side, hasArt, !!text);

    // the decal canvas is square but the region it maps onto usually isn't —
    // pxAspect converts a canvas-x length into the matching canvas-y length so
    // artwork keeps its true aspect on a portrait face (e.g. the 57×89 tag)
    const region = ((data.product && data.product.engraveRegion) || config.engraveRegion || {});
    const wFrac = region.widthFrac || 1;
    const hFrac = region.heightFrac != null ? region.heightFrac : wFrac;
    const pxAspect = modelSize.x > 0 && modelSize.y > 0
      ? (modelSize.x * wFrac) / (modelSize.y * hFrac)
      : 1;

    if (hasArt) {
      const img = s.image;
      const ar = img.width / img.height;
      // slider runs 35–100 but engrave width tops out at ~70% of the region
      // (~10% under the prototype's 78% ceiling) — 100% on the slider means max
      const effSize = 35 + ((art.size - 35) * (70 - 35)) / (100 - 35);
      const box = ENGRAVE_PX * (effSize / 100); // % of engrave region width
      const cap = ENGRAVE_PX * layout.maxHeight; // % of engrave region height
      const h = Math.min((box / ar) * pxAspect, cap);
      const w = (h / pxAspect) * ar;
      const x = (ENGRAVE_PX - w) / 2;
      const y = ENGRAVE_PX * layout.centerY - h / 2;

      // single-tone engraving: draw via offscreen, recolor luminance
      const off = document.createElement('canvas');
      off.width = off.height = ENGRAVE_PX;
      const octx = off.getContext('2d');
      octx.drawImage(img, x, y, w, h);
      octx.globalCompositeOperation = 'source-in';
      octx.fillStyle = engraved; // mask already bakes in the invert direction
      octx.fillRect(0, 0, ENGRAVE_PX, ENGRAVE_PX);
      ctx.drawImage(off, 0, 0);
      b.drawImage(img, x, y, w, h);
      hasContent = true;
    }

    if (text) {
      const font = FONT_STACKS[tCfg && tCfg.font] || FONT_STACKS.Canela;
      // prototype parity: one 89px base regardless of whether art is present —
      // the 81%-of-region-width cap below is the only shrink trigger
      let px = 89;
      ctx.font = px + 'px ' + font;
      while (px > 12 && ctx.measureText(text).width > ENGRAVE_PX * 0.81) {
        px--;
        ctx.font = px + 'px ' + font;
      }
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = engraved;
      const ty = ENGRAVE_PX * layout.nameY;
      // squeeze vertically so glyphs aren't stretched by the non-square region
      ctx.save();
      ctx.translate(ENGRAVE_PX / 2, ty);
      ctx.scale(1, pxAspect);
      ctx.fillText(text, 0, 0);
      ctx.restore();
      b.font = ctx.font;
      b.textAlign = 'center';
      b.textBaseline = 'middle';
      b.save();
      b.translate(ENGRAVE_PX / 2, ty);
      b.scale(1, pxAspect);
      b.fillText(text, 0, 0);
      b.restore();
      hasContent = true;
    }

    b.globalAlpha = 1;
    s.tex.needsUpdate = true;
    s.btex.needsUpdate = true;
    const rules = (data.product && data.product.rules) || config.rules || {};
    const backAllowed = !!(rules.supportsBackArt || rules.doubleSidedUpcharge > 0) &&
      (data.doubleSided || !(rules.doubleSidedUpcharge > 0));
    s.mesh.visible = hasContent && (side === 'front' || backAllowed);
  }

  function loadArtworkImage(side, dataUrl, cb) {
    const img = new Image();
    img.onload = () => { sides[side].image = img; cb(); };
    img.onerror = () => {};
    img.src = dataUrl;
  }

  /* ---------- model loading ---------- */
  const draco = new DRACOLoader().setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/libs/draco/gltf/');
  const gltfLoader = new GLTFLoader().setDRACOLoader(draco);

  let currentModel = null;
  let modelSize = new THREE.Vector3(0.06, 0.09, 0.004); // default tag-ish dims (m)
  let modelUrlLoaded = null;

  function placeholderGeometry() {
    const fam = (latestData?.product?.family || config.family || '').toLowerCase();
    const isMarker = fam.includes('marker');
    const isWallet = /wallet|maverick|alexander|hudson/.test(fam);
    let geo;
    if (isMarker) geo = new THREE.CylinderGeometry(0.02, 0.02, 0.004, 64).rotateX(Math.PI / 2);
    else if (isWallet) geo = new THREE.BoxGeometry(0.07, 0.1, 0.012);
    else {
      const w = 0.057;
      const h = 0.089;
      const shape = new THREE.Shape();
      shape.moveTo(-w / 2, -h / 2);
      shape.lineTo(w / 2 - 0.007, -h / 2);
      shape.lineTo(w / 2, -h / 2 + 0.01);
      shape.lineTo(w / 2, h / 2 - 0.012);
      shape.lineTo(w / 2 - 0.009, h / 2);
      shape.lineTo(-w / 2, h / 2);
      shape.closePath();
      const hole = new THREE.Path();
      hole.absarc(0.012, 0.028, 0.0026, 0, Math.PI * 2, true);
      shape.holes.push(hole);
      geo = new THREE.ExtrudeGeometry(shape, {
        depth: 0.0035, steps: 1, bevelEnabled: true,
        bevelThickness: 0.0003, bevelSize: 0.0003, bevelSegments: 2, curveSegments: 24,
      });
      geo.translate(0, 0, -0.00175);
    }
    const face = new THREE.MeshStandardMaterial({ color: 0x985f3c, roughness: 0.85, metalness: 0.02, side: THREE.DoubleSide });
    const edge = new THREE.MeshStandardMaterial({ color: 0x69432f, roughness: 0.9, metalness: 0.01 });
    const mesh = new THREE.Mesh(geo, isMarker || isWallet ? face : [face, edge]);
    mesh.userData.dcFallback = true;
    mesh.userData.dcFamily = fam;
    return mesh;
  }

  function setModel(object) {
    const firstModel = !currentModel;
    if (currentModel) productGroup.remove(currentModel);
    currentModel = object;
    meshAreas.clear();
    productGroup.add(object);
    const box = new THREE.Box3().setFromObject(object);
    box.getSize(modelSize);
    // center model
    const center = box.getCenter(new THREE.Vector3());
    object.position.sub(center);
    layoutDecals();
    fitCamera();
    if (firstModel) setAzimuthPolar(-0.55, Math.PI / 2.1);
    updateModelNote();
  }

  // "55.9 × 87.3 mm · CAD reference" (+ "Sample weight: …" when configured)
  function updateModelNote() {
    if (!currentModel) return;
    const dims = `${(modelSize.x * 1000).toFixed(1)} × ${(modelSize.y * 1000).toFixed(1)} mm`;
    const weight = latestData?.product?.sampleWeight;
    $('#dc-model-note').textContent =
      `${dims} · ${currentModel.userData.dcFallback ? 'illustrative model' : 'CAD reference'}` +
      (weight ? `\nSample weight: ${weight}` : '');
  }

  // raycast the actual surface along the engraving centerline — Maverick's
  // bounding box includes the spring clip, which protrudes past the rear plate
  // and would leave the decal floating in space; engraveLayout's centerY aims
  // below the clip. Wallet-family only: flat products keep bounding-box depth.
  const surfaceRay = new THREE.Raycaster();
  const meshAreas = new Map(); // per-mesh XY area cache — cleared on model swap
  function faceDepth(name, o) {
    const prod = (latestData && latestData.product) || config;
    const f = String(prod.family || config.family || '').toLowerCase().replace(/[^a-z]/g, '');
    if (LAYOUT_KINDS[f] !== 'wallet') return null;
    const sign = name === 'front' ? 1 : -1;
    const tCfg = latestData && latestData.text && latestData.text[name];
    const hasText = !!(tCfg && tCfg.enabled && String(tCfg.text || '').trim());
    const hasArt = !!(latestData && latestData.artwork && latestData.artwork[name] && latestData.artwork[name].dataUrl);
    const layout = engraveLayout(latestData || { product: prod }, name, hasArt, hasText);
    const cy = layout.centerY != null ? layout.centerY : 0.5;
    const y = (0.5 - cy) * modelSize.y;
    surfaceRay.set(
      new THREE.Vector3(0, y, sign * Math.max(modelSize.z * 3, 0.01)),
      new THREE.Vector3(0, 0, -sign)
    );
    const hits = surfaceRay.intersectObject(currentModel, true);
    // First hit per mesh = that part's outermost surface along the ray, but
    // raycasts ignore occlusion — interior faces of far parts hit too. So keep
    // only large-area parts (the plates, not screws/clip) and take the
    // OUTERMOST hit among them: engraving lands on the main plate face.
    const seen = new Set();
    const candidates = [];
    let maxArea = 0;
    for (const h of hits) {
      if (seen.has(h.object)) continue;
      seen.add(h.object);
      if (sign > 0 ? h.point.z <= 0 : h.point.z >= 0) continue;
      let area = meshAreas.get(h.object);
      if (area == null) {
        const bs = new THREE.Box3().setFromObject(h.object).getSize(new THREE.Vector3());
        area = bs.x * bs.y;
        meshAreas.set(h.object, area);
      }
      candidates.push({ az: Math.abs(h.point.z), area });
      if (area > maxArea) maxArea = area;
    }
    let best = null;
    for (const c of candidates) {
      if (c.area < maxArea * 0.5) continue;
      if (best == null || c.az > best) best = c.az;
    }
    return best;
  }

  function layoutDecals() {
    const region = ((latestData && latestData.product) || config).engraveRegion || {};
    const w = region.widthFrac || 1;
    const hFrac = region.heightFrac != null ? region.heightFrac : w;
    const d = Math.max(modelSize.z, 0.001) / 2;
    for (const [name, s] of Object.entries(sides)) {
      // zFrac stays as an explicit override; otherwise raycast the real face
      // depth so engraving sits on the plate, not on clip-space
      const o = Object.assign({}, region, region[name] || {});
      let depth = d;
      if (o.zFrac != null) depth = d * o.zFrac;
      else {
        const hit = faceDepth(name, o);
        if (hit != null) depth = hit;
      }
      s.mesh.scale.set(modelSize.x * w, modelSize.y * hFrac, 1);
      s.mesh.position.set(0, 0, name === 'front' ? depth + 0.0003 : -depth - 0.0003);
      s.mesh.rotation.y = name === 'front' ? 0 : Math.PI;
    }
  }

  let lastFitR = null;
  function fitCamera() {
    const r = Math.max(modelSize.x, modelSize.y, modelSize.z) * 0.5;
    controls.minDistance = r * 1.6;
    controls.maxDistance = r * 4.0;
    camera.near = Math.max(r / 100, 0.0001);
    camera.far = Math.max(100, r * 12);
    // keep the user's zoom across model/finish swaps — rescale, don't reset
    // first load starts fully zoomed out (maxDistance) so users can only zoom in
    const dist = camera.position.length();
    camera.position.setLength(lastFitR ? dist * (r / lastFitR) : controls.maxDistance);
    lastFitR = r;
    camera.updateProjectionMatrix();
  }

  function loadModel(url) {
    if (!url || url === modelUrlLoaded) return;
    modelUrlLoaded = url;
    loadingEl.hidden = false;
    gltfLoader.load(
      url,
      (gltf) => {
        if (url !== modelUrlLoaded) return;
        setModel(gltf.scene);
        loadingEl.hidden = true;
        container.dataset.ready = 'true';
        applyFinish(currentFinish);
      },
      undefined,
      () => {
        if (url !== modelUrlLoaded) return;
        setModel(placeholderGeometry());
        applyFinish(currentFinish);
        loadingEl.hidden = true;
        container.dataset.ready = 'true';
        $('#dc-model-note').textContent = 'Model unavailable · illustrative preview shown';
      }
    );
  }

  /* ---------- finish switching ---------- */
  let currentFinish = null;
  function hexToColor(v) {
    if (!v) return null;
    const c = new THREE.Color();
    try { c.set(v); return c; } catch { return null; }
  }

  // Rec.709 luminance (0–1) of a '#hex' or 'rgb(r,g,b)' swatch, in sRGB space
  function srgbLuminance(v) {
    if (!v) return null;
    const s = String(v).trim();
    let r, g, b;
    const hex = /^#([0-9a-f]{3,8})$/i.exec(s);
    const rgb = /^rgba?\(\s*([^)]+)\)$/i.exec(s);
    if (hex) {
      let h = hex[1];
      if (h.length <= 4) h = h.slice(0, 3).split('').map((c) => c + c).join('');
      r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16);
    } else if (rgb) {
      [r, g, b] = rgb[1].split(',').map((n) => parseFloat(n));
    } else {
      return null;
    }
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  function applyFinish(variant) {
    if (!variant || !currentModel) return;
    const color = hexToColor(variant.swatchColor);
    const product = (latestData && latestData.product) || config;
    const probe = materialProbe(variant.options?.[0], variant, product);
    const isMarker = /marker/.test(String(product?.family || '').toLowerCase());
    // markers are all anodized aluminum; other families fall back to the probe
    const metallic = isMarker || METAL_RE.test(probe) || /gold|silver/.test(probe);
    const organic = ORGANIC_RE.test(probe);
    currentModel.traverse((o) => {
      if (!o.isMesh || !o.material) return;
      const materials = Array.isArray(o.material) ? o.material : [o.material];
      if (!o.userData.dcFallback) {
        // Production GLBs are baked per finish — unlit photographic faces and
        // authored trim colors are never recolored. Bare lit materials (edge/
        // trim walls) get a grain bump so the edge reads as material, not flat.
        // 'bumpMap' in material gates unlit ones (no bumpMap uniform → crash).
        materials.forEach((material) => {
          if (material.userData.dcGrain || material.map || !('bumpMap' in material)) return;
          material.bumpMap = edgeBump(probe);
          material.bumpScale = organic ? 0.05 : 0.02;
          material.needsUpdate = true;
          material.userData.dcGrain = true;
        });
        return;
      }
      if (!color) return;
      materials.forEach((material, index) => {
        // 'bumpMap' in material gates unlit materials (KHR_materials_unlit →
        // MeshBasicMaterial): their shader has no bumpMap uniform, so assigning
        // one crashes refreshUniformsCommon every frame
        if (!material.userData.dcGrain) {
          if ('bumpMap' in material && !material.bumpMap) {
            material.bumpMap = grainTex;
            material.needsUpdate = true;
          }
          material.userData.dcGrain = true;
        }
        material.bumpScale = organic ? 0.08 : metallic ? 0.07 : 0.05;
        if (!material.color) return;
        // bake the grain into the tint — the map carries the finish color
        if (material.userData.dcMottleHex !== variant.swatchColor) {
          material.map = mottledMap(variant.swatchColor);
          material.userData.dcMottle = true;
          material.userData.dcMottleHex = variant.swatchColor;
          material.needsUpdate = true;
        }
        material.color.set('#ffffff'); // tint lives in the map now
        if (index === 1) material.color.multiplyScalar(0.65);
        material.metalness = metallic ? (isMarker ? 0.38 : 0.6) : 0.02;
        material.roughness = metallic ? (isMarker ? 0.62 : 0.42) : index === 1 ? 0.9 : 0.82;
      });
    });
  }

  /* ---------- camera presets & controls ---------- */
  let cameraAnim = null;
  function setAzimuthPolar(az, pol) {
    const r = camera.position.length();
    camera.position.set(
      r * Math.sin(pol) * Math.sin(az),
      r * Math.cos(pol),
      r * Math.sin(pol) * Math.cos(az)
    );
    controls.update();
  }
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // eased orbit to a face — short way around the azimuth circle
  function flyTo(az, pol, dur = 700) {
    if (reduceMotion.matches) { setAzimuthPolar(az, pol); return; }
    const p = camera.position;
    const fromAz = Math.atan2(p.x, p.z);
    const fromPol = Math.acos(THREE.MathUtils.clamp(p.y / p.length(), -1, 1));
    const dAz = Math.atan2(Math.sin(az - fromAz), Math.cos(az - fromAz));
    cameraAnim = { fromAz, fromPol, toAz: fromAz + dAz, toPol: pol, t0: performance.now(), dur };
  }
  function stepCameraAnim(now) {
    const t = Math.min((now - cameraAnim.t0) / cameraAnim.dur, 1);
    const e = 1 - Math.pow(1 - t, 3);
    setAzimuthPolar(
      cameraAnim.fromAz + (cameraAnim.toAz - cameraAnim.fromAz) * e,
      cameraAnim.fromPol + (cameraAnim.toPol - cameraAnim.fromPol) * e
    );
    if (t >= 1) cameraAnim = null;
  }
  const presets = {
    front: () => flyTo(0, Math.PI / 2),
    back: () => flyTo(Math.PI, Math.PI / 2),
    edge: () => flyTo(Math.PI / 2, Math.PI / 2.15),
  };
  function setActiveViewBtn(view) {
    $$viewBtns().forEach((b) => {
      const on = b.dataset.view === view;
      b.classList.toggle('active', on);
      b.setAttribute('aria-pressed', String(on));
    });
  }
  $$viewBtns().forEach((btn) =>
    btn.addEventListener('click', () => {
      setActiveViewBtn(btn.dataset.view);
      setAutoRotate(false);
      presets[btn.dataset.view]?.();
    })
  );
  function $$viewBtns() { return Array.from(root.querySelectorAll('#dc-model-controls button')); }

  const rotBtn = $('#dc-rotation');
  const pauseIcon = '<svg width="8" height="10" viewBox="0 0 8 10" fill="currentColor" focusable="false"><rect width="3" height="10"/><rect x="5" width="3" height="10"/></svg>';
  const playIcon = '<svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor" focusable="false"><path d="M0 0l9 5-9 5z"/></svg>';
  function setAutoRotate(on) {
    controls.autoRotate = on;
    if (!rotBtn) return;
    rotBtn.setAttribute('aria-pressed', String(on));
    rotBtn.innerHTML = on
      ? `<span aria-hidden="true">${pauseIcon}</span> Pause`
      : `<span aria-hidden="true">${playIcon}</span> Rotate`;
  }
  rotBtn?.addEventListener('click', () => setAutoRotate(!controls.autoRotate));
  // grabbing or zooming the model stops the showcase spin and any in-flight pan
  controls.addEventListener('start', () => { cameraAnim = null; setAutoRotate(false); });

  container.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') { cameraAnim = null; setAutoRotate(false); rotateStep(0.15); }
    if (e.key === 'ArrowRight') { cameraAnim = null; setAutoRotate(false); rotateStep(-0.15); }
  });
  function rotateStep(dAz) {
    const az = Math.atan2(camera.position.x, camera.position.z) + dAz;
    const r = camera.position.length();
    camera.position.set(r * Math.sin(az), camera.position.y, r * Math.cos(az));
    controls.update();
  }

  const detailBtn = $('#dc-detail-view');
  detailBtn?.addEventListener('click', () => {
    const on = root.classList.toggle('dc-detail-mode');
    detailBtn.setAttribute('aria-pressed', String(on));
    onResize();
  });

  /* ---------- state updates ---------- */
  let latestData = null;
  let selectedSide = 'front';
  window.addEventListener('dashclip:update', (e) => {
    const data = e.detail;
    latestData = data;
    // prototype parity: solid-metal wallets (Maverick) get the brighter studio
    // environment, cooler key/rim and a tighter lens
    const prod = (data && data.product) || config;
    const fam = String(prod.family || '').toLowerCase().replace(/[^a-z]/g, '');
    const solidMetal = LAYOUT_KINDS[fam] === 'wallet' && /aluminum/i.test(String(prod.material || ''));
    scene.environment = solidMetal ? metalEnv : roomEnv;
    hemi.intensity = solidMetal ? 0.85 : 1.7;
    key.intensity = solidMetal ? 2.7 : 2.4;
    key.color.set(solidMetal ? '#ffffff' : '#fff4df');
    rim.intensity = solidMetal ? 2.2 : 2.0;
    rim.color.set(solidMetal ? '#eef3ff' : '#dbe7ff');
    updateModelNote();
    if (currentModel) layoutDecals(); // engraving centerline shifts with text — re-aim surface depth
    if (data.side !== selectedSide) {
      selectedSide = data.side;
      setAutoRotate(false);
      setActiveViewBtn(data.side);
      presets[data.side]?.();
    }
    if (data.variant && data.variant.modelUrl) loadModel(data.variant.modelUrl);
    else if (data.variant && (modelUrlLoaded ||
      (currentModel?.userData.dcFallback && currentModel.userData.dcFamily !== String(data.product?.family || '').toLowerCase()))) {
      // switched to a product/variant without a GLB — back to the illustrative model
      modelUrlLoaded = null;
      setModel(placeholderGeometry());
    }
    if (data.variant && data.variant.sku) {
      // finish label handled by main script; recolor if swatch color provided
      applyFinish(data.variant);
    }
    currentFinish = data.variant;
    for (const side of ['front', 'back']) {
      const art = data.artwork[side];
      if (art && art.dataUrl && !sides[side].image) {
        loadArtworkImage(side, art.dataUrl, () => drawEngrave(side, latestData));
      } else if (art && art.dataUrl && sides[side].image?.src !== art.dataUrl) {
        loadArtworkImage(side, art.dataUrl, () => drawEngrave(side, latestData));
      } else {
        if (!art || !art.dataUrl) sides[side].image = null;
        drawEngrave(side, data);
      }
    }
    // text can rasterize before the display font loads — warm it, then redraw.
    // drawEngrave is idempotent over latestData so the extra pass is safe
    if (data.text && document.fonts && document.fonts.load) {
      const stacks = [...new Set(['front', 'back']
        .map((sd) => data.text[sd])
        .filter((t) => t && t.enabled && t.text)
        .map((t) => FONT_STACKS[t.font] || FONT_STACKS.Canela))];
      if (stacks.length) Promise.all(stacks.map((st) => document.fonts.load('89px ' + st)))
        .then(() => { for (const side of ['front', 'back']) drawEngrave(side, latestData); })
        .catch(() => {});
    }
  });

  // Frame the edited face whenever the shopper touches the configurator.
  // Pauses the showcase spin and flies to the selected side — unless the
  // camera already faces it or the user is mid-drag.
  let dragging = false;
  controls.addEventListener('end', () => { dragging = false; });
  controls.addEventListener('start', () => { dragging = true; });
  const faceAzimuth = (side) => (side === 'back' ? Math.PI : 0);
  const FACE_TOLERANCE = Math.PI / 6; // ~30° of drift counts as "already facing"
  function facingSide(side) {
    const az = Math.atan2(camera.position.x, camera.position.z);
    const d = Math.atan2(Math.sin(faceAzimuth(side) - az), Math.cos(faceAzimuth(side) - az));
    return Math.abs(d) < FACE_TOLERANCE;
  }
  const configurator = $('#dc-configuration');
  ['click', 'input', 'change'].forEach((type) =>
    configurator?.addEventListener(type, () => {
      if (dragging) return;
      const side = selectedSide === 'back' ? 'back' : 'front';
      // spinning counts as never parked on a face — always re-frame on interaction
      if (!controls.autoRotate && facingSide(side)) return;
      setAutoRotate(false);
      setActiveViewBtn(side);
      presets[side]();
    }, true)
  );

  /* ---------- capture ---------- */
  window.dashclip3d = {
    capture(side) {
      const savedPos = camera.position.clone();
      const savedAnim = cameraAnim;
      const savedSpin = controls.autoRotate;
      cameraAnim = null;
      controls.autoRotate = false;
      if (side === 'front' || side === 'back') {
        setAzimuthPolar(side === 'back' ? Math.PI : 0, Math.PI / 2);
      }
      renderer.render(scene, camera);
      const url = canvas.toDataURL('image/png');
      camera.position.copy(savedPos);
      controls.autoRotate = savedSpin;
      controls.update();
      renderer.render(scene, camera);
      cameraAnim = savedAnim;
      return url;
    },
    loadModel,
  };

  /* ---------- resize & loop ---------- */
  function onResize() {
    const w = container.clientWidth || 600;
    const h = container.clientHeight || w / 1.15;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    // canvas extends behind the whole caption (eyebrow + title) — lift the model into the visible area above it
    const caption = root.querySelector('.stage-caption');
    if (caption) container.style.setProperty('--dc-caption-overlap', caption.offsetHeight + 'px');
    const overlap = Math.abs(parseFloat(getComputedStyle(container).marginBottom)) || 0;
    camera.setViewOffset(w, h, 0, overlap / 2, w, h);
    camera.updateProjectionMatrix();
  }
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(container);
  const stageCaption = root.querySelector('.stage-caption');
  if (stageCaption) resizeObserver.observe(stageCaption);
  onResize();

  function tick() {
    requestAnimationFrame(tick);
    if (cameraAnim) stepCameraAnim(performance.now());
    controls.update();
    renderer.render(scene, camera);
  }
  tick();

  // initial model: placeholder until a GLB arrives via update
  setModel(placeholderGeometry());
  loadingEl.hidden = true;
  container.dataset.ready = 'true';
  const detail = $('#dc-detail-view');
  if (detail) detail.hidden = false;

  // If the main script initialized first, re-emit current state so the
  // viewer picks up finish/artwork that was set before we subscribed.
  if (window.dashclipStudio && window.dashclipStudio.syncUI) {
    window.dashclipStudio.syncUI();
  }
})();
